import { type ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Main,
  Root,
  SideNav,
  SideNavToggleButton,
  TopNav,
  TopNavStart
} from '@atlaskit/navigation-system';
import { useExpandSideNav } from '@atlaskit/navigation-system/layout/side-nav';
import { SidebarNav } from '@app/layout/SidebarNav';
import { broadcastSignedOut } from '@shared/api/apiConfig';
import { clearAuthSession } from '@shared/lib/auth';
import MockTopBar from '@shared/ui/MockTopBar';

interface AppShellProps {
  children: ReactNode;
}

function PersistentSideNav({ children }: { children: ReactNode }) {
  const expandSideNav = useExpandSideNav({ trigger: 'programmatic' });

  useEffect(() => {
    expandSideNav();
  }, [expandSideNav]);

  return (
    <SideNav
      id="app-primary-side-nav"
      label="Primary navigation"
      onCollapse={({ trigger }) => {
        // Mobile treats the side nav as a dismissible drawer that closes on outside
        // taps. We want it to stay open by default on mobile too, so re-expand it
        // when that's what collapsed it. Explicit toggle/keyboard collapses are left alone.
        if (trigger === 'click-outside-on-mobile') {
          expandSideNav();
        }
      }}
    >
      {children}
    </SideNav>
  );
}

export function AppShell({ children }: AppShellProps) {
  const navigate = useNavigate();

  const handleLogout = () => {
    clearAuthSession();
    broadcastSignedOut();
    navigate('/auth/login');
  };

  return (
    <Root skipLinksLabel="App navigation" defaultSideNavCollapsed={false}>
      <TopNav>
        <TopNavStart
          sideNavToggleButton={
            <SideNavToggleButton
              collapseLabel="Collapse sidebar"
              expandLabel="Expand sidebar"
            />
          }
        >
          <MockTopBar />
        </TopNavStart>
      </TopNav>

      <PersistentSideNav>
        <SidebarNav onLogout={handleLogout} />
      </PersistentSideNav>

      <Main>
        <div className="content-shell">{children}</div>
      </Main>
    </Root>
  );
}
